import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calculator, TrendingUp, Users, Home, RefreshCw, X, ExternalLink, ArrowUp, ArrowDown, ArrowUpDown, ChevronDown, ChevronRight } from 'lucide-react';
import { fetchInvestmentCalculatorAnalytics, fetchEventDetails } from '../api/analyticsApi';
import { syncVirtualContact } from '../../../api/contact';
import toast from 'react-hot-toast';

// Friendly labels + stable colours for every Investor Tools calculator mode.
const MODE_LABELS = {
    single: 'Single property',
    block: 'Block of properties',
    equity: 'Equity & borrowing',
    smsf: 'Buy Through SMSF',
    mortgage: 'Borrowing Capacity',
    buy_vs_rent: 'Buy vs Rent',
    renovation: 'Renovation ROI',
    land_tax: 'Land Tax',
    hidden_costs: 'Hidden Costs',
    holding_cost: 'Holding Cost',
    depreciation: 'Depreciation',
    rent_increase: 'Rent Increase',
    investment_score: 'Investment Score',
    comparison: 'Compare Properties',
    negotiation: 'Negotiation Assistant',
    portfolio_health: 'Portfolio Health',
};
const MODE_ORDER = Object.keys(MODE_LABELS);
const MODE_BAR_COLORS = [
    'bg-blue-500', 'bg-purple-500', 'bg-teal-500', 'bg-emerald-500', 'bg-amber-500',
    'bg-rose-500', 'bg-indigo-500', 'bg-cyan-500', 'bg-pink-500', 'bg-lime-500',
    'bg-orange-500', 'bg-sky-500', 'bg-fuchsia-500', 'bg-violet-500', 'bg-green-500',
    'bg-red-500',
];
const modeLabel = (key) => MODE_LABELS[key] || (key ? String(key).replace(/_/g, ' ') : '—');
const modeColor = (key) => {
    const idx = MODE_ORDER.indexOf(key);
    return MODE_BAR_COLORS[(idx >= 0 ? idx : MODE_ORDER.length) % MODE_BAR_COLORS.length];
};

const formatCurrency = (val) => {
    if (val === null || val === undefined || Number.isNaN(val)) return '—';
    return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        maximumFractionDigits: 0,
    }).format(val);
};

const formatPercent = (val) => {
    if (val === null || val === undefined || Number.isNaN(val)) return '—';
    return `${Number(val).toFixed(2)}%`;
};

const formatRelative = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const m = Math.floor(diffMs / 60000);
    if (m < 60) return `${Math.max(1, m)}m ago`;
    const h = Math.floor(diffMs / 3600000);
    if (h < 24) return `${h}h ago`;
    const days = Math.floor(diffMs / 86400000);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
};

const mapEventToRow = (ev) => {
    const props = ev.properties_json || {};
    return {
        event_id: ev.event_id,
        occurred_at: ev.occurred_at,
        buyer: ev.buyer || null,
        mode: props.mode,
        price: props.price,
        annual_cash_flow: props.annual_cash_flow,
        gross_yield_pct: props.gross_yield_pct,
        net_yield_pct: props.net_yield_pct,
        property: ev.property,
    };
};

const SortHeader = ({ label, column, sortBy, sortDir, onSort, align = 'right' }) => {
    const active = sortBy === column;
    return (
        <button
            type="button"
            onClick={() => onSort(column)}
            className={`inline-flex items-center gap-1 uppercase hover:text-gray-900 transition-colors ${active ? 'text-blue-600 font-semibold' : ''} ${align === 'right' ? 'flex-row-reverse' : ''}`}
            title={`Sort by ${label.toLowerCase()}`}
        >
            {label}
            {active
                ? (sortDir === 'desc' ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />)
                : <ArrowUpDown className="w-3 h-3 opacity-40" />}
        </button>
    );
};

const StatCard = ({ icon: Icon, label, value, sub, tint = 'blue', onClick }) => {
    const tints = {
        blue: 'bg-blue-50 text-blue-600',
        green: 'bg-emerald-50 text-emerald-600',
        purple: 'bg-purple-50 text-purple-600',
        amber: 'bg-amber-50 text-amber-600',
    };
    const clickable = typeof onClick === 'function';
    return (
        <div
            className={`bg-white rounded-lg shadow p-4 flex items-start gap-3 transition ${
                clickable ? 'cursor-pointer hover:shadow-md hover:bg-gray-50' : ''
            }`}
            onClick={onClick}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
        >
            <div className={`p-2 rounded-lg flex-shrink-0 ${tints[tint] || tints.blue}`}>
                <Icon className="w-5 h-5" />
            </div>
            <div className="min-w-0">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</p>
                <p className="text-2xl font-bold text-gray-900 leading-tight">{value}</p>
                {sub && <p className="text-xs text-gray-500 mt-0.5 truncate">{sub}</p>}
            </div>
        </div>
    );
};

const InvestmentCalculatorAnalytics = ({ startDate, endDate }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const [redirectingBuyer, setRedirectingBuyer] = useState(false);
    const [drilldown, setDrilldown] = useState(null);
    const [drilldownRows, setDrilldownRows] = useState([]);
    const [drilldownLoading, setDrilldownLoading] = useState(false);
    const [sortBy, setSortBy] = useState('opens'); // 'opens' | 'calculations' | 'last_at'
    const [sortDir, setSortDir] = useState('desc'); // 'desc' | 'asc'
    const [buyersOpen, setBuyersOpen] = useState(false);

    const handleSort = (column) => {
        if (sortBy === column) {
            setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
        } else {
            setSortBy(column);
            setSortDir('desc');
        }
    };

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        fetchInvestmentCalculatorAnalytics(startDate, endDate)
            .then((res) => { if (!cancelled) setData(res); })
            .catch((err) => { if (!cancelled) setError(err.message || 'Failed to load'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [startDate, endDate]);

    useEffect(() => {
        if (!drilldown) {
            setDrilldownRows([]);
            return undefined;
        }

        let cancelled = false;
        setDrilldownLoading(true);
        setDrilldownRows([]);

        const eventName = drilldown.filter === 'opens'
            ? 'investment_calculator_opened'
            : 'investment_calculator_calculated';

        fetchEventDetails(startDate, endDate, eventName, null, null, 1000, null, drilldown.mode || null)
            .then((events) => {
                if (cancelled) return;
                let rows = (events || []).map(mapEventToRow);
                if (drilldown.filter === 'signed_in') {
                    rows = rows.filter((r) => r.buyer);
                }
                setDrilldownRows(rows);
            })
            .catch(() => {
                if (!cancelled) toast.error('Could not load activity details.');
            })
            .finally(() => {
                if (!cancelled) setDrilldownLoading(false);
            });

        return () => { cancelled = true; };
    }, [drilldown, startDate, endDate]);

    const openBuyer = async (buyer) => {
        if (!buyer || redirectingBuyer) return;
        setRedirectingBuyer(true);
        const toastId = toast.loading(`Opening ${buyer.name || buyer.email}…`);
        try {
            const response = await syncVirtualContact(`buyer_${buyer.id}`);
            const contactId = response?.data?.id;
            if (!contactId) throw new Error('Could not resolve contact');
            toast.dismiss(toastId);
            navigate(`/contacts/${contactId}?tab=activity`, {
                state: { returnTo: '/analytics?view=calculators', returnLabel: 'Calculators' },
            });
        } catch (err) {
            toast.error('Could not open buyer profile.', { id: toastId });
        } finally {
            setRedirectingBuyer(false);
        }
    };

    if (loading) {
        return (
            <div className="bg-white rounded-lg shadow p-6 flex items-center justify-center h-40">
                <RefreshCw className="w-5 h-5 text-blue-500 animate-spin mr-2" />
                <span className="text-gray-500 text-sm">Loading investment calculator analytics…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-2">Investment Calculator</h2>
                <p className="text-rose-600 text-sm">{error}</p>
            </div>
        );
    }

    if (!data) return null;

    const {
        total_opens = 0,
        total_calculations = 0,
        unique_users = 0,
        authenticated_users = 0,
        mode_breakdown = {},
        per_calculator = [],
        averages = {},
        top_properties = [],
        buyer_summaries = [],
    } = data;

    // One row per calculator, busiest first (opened + calculated), zeros last.
    const perCalcRows = [...per_calculator].sort(
        (a, b) => (b.calculations + b.selections) - (a.calculations + a.selections)
    );

    // Build one row per calculator that has usage, biggest first.
    const modeEntries = Object.entries(mode_breakdown)
        .filter(([, v]) => (v || 0) > 0)
        .sort((a, b) => b[1] - a[1]);
    const totalModes = modeEntries.reduce((s, [, v]) => s + v, 0);
    const modeRows = modeEntries.map(([key, count]) => ({
        key,
        label: modeLabel(key),
        count,
        pct: totalModes > 0 ? Math.round((count / totalModes) * 100) : 0,
        color: modeColor(key),
    }));
    const anonCount = Math.max(0, unique_users - authenticated_users);

    const sortedBuyers = [...buyer_summaries].sort((a, b) => {
        let av;
        let bv;
        if (sortBy === 'last_at') {
            av = a.last_at ? new Date(a.last_at).getTime() : 0;
            bv = b.last_at ? new Date(b.last_at).getTime() : 0;
        } else {
            av = a[sortBy] || 0;
            bv = b[sortBy] || 0;
        }
        return sortDir === 'desc' ? bv - av : av - bv;
    });

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <Calculator className="w-5 h-5 text-blue-600" />
                        Investment Calculator
                    </h3>
                    <p className="text-sm text-gray-500">How buyers are using the calculator on the portal</p>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard
                    icon={Calculator}
                    label="Opens"
                    value={total_opens}
                    sub={`${unique_users} unique users`}
                    tint="blue"
                    onClick={total_opens > 0 ? () => setDrilldown({ title: 'Calculator opens — all activity', filter: 'opens' }) : undefined}
                />
                <StatCard
                    icon={TrendingUp}
                    label="Calculations"
                    value={total_calculations}
                    sub="debounced finals"
                    tint="green"
                    onClick={total_calculations > 0 ? () => setDrilldown({ title: 'All calculations', filter: 'calculations' }) : undefined}
                />
                <StatCard
                    icon={Users}
                    label="Signed-in users"
                    value={authenticated_users}
                    sub={`${anonCount} anon`}
                    tint="purple"
                    onClick={authenticated_users > 0 ? () => setDrilldown({ title: 'Signed-in user activity', filter: 'signed_in' }) : undefined}
                />
                <StatCard
                    icon={Home}
                    label="Avg deal cashflow"
                    value={formatCurrency(averages.avg_annual_cash_flow)}
                    sub={averages.avg_price ? `~${formatCurrency(averages.avg_price)} avg price` : null}
                    tint="amber"
                    onClick={total_calculations > 0 ? () => setDrilldown({ title: 'Deal cashflow — all calculations', filter: 'calculations' }) : undefined}
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="bg-white rounded-lg shadow p-5">
                    <h4 className="text-sm font-bold text-gray-900 mb-3">Usage by calculator</h4>
                    {totalModes === 0 ? (
                        <p className="text-sm text-gray-500">No calculations yet.</p>
                    ) : (
                        <>
                            <div className="flex h-3 rounded-full overflow-hidden bg-gray-100 mb-3">
                                {modeRows.map((m) => (
                                    <div key={m.key} className={m.color} style={{ width: `${m.pct}%` }} title={`${m.label}: ${m.count}`} />
                                ))}
                            </div>
                            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                                {modeRows.map((m) => (
                                    <div key={m.key} className="flex items-center justify-between text-sm">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${m.color}`} />
                                            <span className="text-gray-700 truncate">{m.label}</span>
                                        </div>
                                        <span className="font-semibold text-gray-900 flex-shrink-0">{m.count} ({m.pct}%)</span>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    <div className="mt-5 pt-4 border-t border-gray-100 space-y-1.5 text-sm">
                        <div className="flex justify-between"><span className="text-gray-500">Avg gross yield</span><span className="font-medium text-gray-900">{formatPercent(averages.avg_gross_yield_pct)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Avg net yield</span><span className="font-medium text-gray-900">{formatPercent(averages.avg_net_yield_pct)}</span></div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow p-5 lg:col-span-2">
                    <h4 className="text-sm font-bold text-gray-900 mb-3">Top properties analysed</h4>
                    {top_properties.length === 0 ? (
                        <p className="text-sm text-gray-500">No calculator events have been linked to a property yet.</p>
                    ) : (
                        <ul className="divide-y divide-gray-100">
                            {top_properties.map((p) => {
                                const displayAddress = p.full_address || p.address || `Property #${p.id}`;
                                return (
                                <li
                                    key={p.id}
                                    className="py-2.5 flex items-center justify-between gap-3 cursor-pointer hover:bg-gray-50 -mx-2 px-2 rounded"
                                    onClick={() => navigate(`/properties/${p.id}?tab=analytics`)}
                                >
                                    <div className="min-w-0">
                                        <p className="text-sm font-semibold text-gray-900 truncate">
                                            {displayAddress}
                                        </p>
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                        <p className="text-sm font-semibold text-gray-900">{p.calculations} calcs</p>
                                        <p className="text-xs text-gray-500">{p.opens} opens · {p.unique_users} users</p>
                                    </div>
                                </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-lg shadow p-5">
                <h4 className="text-sm font-bold text-gray-900 mb-1">Per-calculator breakdown</h4>
                <p className="text-xs text-gray-500 mb-3">Every Investor Tools calculator — opens, calculations, unique users and enquiries in this period. Click a calculator to see who used it and what they calculated.</p>
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead className="text-xs uppercase text-gray-500 border-b border-gray-200">
                            <tr>
                                <th className="text-left py-2 pr-3">Calculator</th>
                                <th className="text-right py-2 pr-3">Opened</th>
                                <th className="text-right py-2 pr-3">Calculations</th>
                                <th className="text-right py-2 pr-3">Unique users</th>
                                <th className="text-right py-2 pr-3">Enquiries</th>
                                <th className="text-right py-2 pr-3">Last used</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {perCalcRows.map((c) => {
                                const idle = (c.calculations + c.selections) === 0;
                                const clickable = c.calculations > 0;
                                return (
                                    <tr
                                        key={c.mode}
                                        className={`${idle ? 'opacity-50' : ''} ${clickable ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                                        onClick={clickable ? () => setDrilldown({
                                            title: `${modeLabel(c.mode)} — calculations`,
                                            filter: 'calculations',
                                            mode: c.mode,
                                        }) : undefined}
                                        title={clickable ? `See who used ${modeLabel(c.mode)} and what they calculated` : undefined}
                                    >
                                        <td className="py-2 pr-3">
                                            <div className="flex items-center gap-2">
                                                <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${modeColor(c.mode)}`} />
                                                <span className={`text-gray-900 font-medium ${clickable ? 'hover:text-blue-600' : ''}`}>{modeLabel(c.mode)}</span>
                                            </div>
                                        </td>
                                        <td className="py-2 pr-3 text-right text-gray-700">{c.selections}</td>
                                        <td className="py-2 pr-3 text-right text-gray-900 font-semibold">{c.calculations}</td>
                                        <td className="py-2 pr-3 text-right text-gray-700">{c.unique_users}</td>
                                        <td className="py-2 pr-3 text-right text-gray-700">{c.enquiries}</td>
                                        <td className="py-2 pr-3 text-right text-gray-500">{c.last_at ? formatRelative(c.last_at) : '—'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow p-5">
                <button
                    type="button"
                    onClick={() => setBuyersOpen((v) => !v)}
                    className="w-full flex items-center justify-between text-left"
                    aria-expanded={buyersOpen}
                >
                    <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                        {buyersOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                        All buyers — calculator activity
                    </h4>
                    <p className="text-xs text-gray-500">
                        {buyer_summaries.length} signed-in buyer{buyer_summaries.length !== 1 ? 's' : ''} · {buyersOpen ? 'Click a row to open their CRM profile' : 'Click to expand'}
                    </p>
                </button>
                {!buyersOpen ? null : buyer_summaries.length === 0 ? (
                    <p className="text-sm text-gray-500 mt-3">No signed-in buyers have used the calculator in this period.</p>
                ) : (
                    <div className="overflow-x-auto mt-3">
                        <table className="min-w-full text-sm">
                            <thead className="text-xs uppercase text-gray-500 border-b border-gray-200">
                                <tr>
                                    <th className="text-left py-2 pr-3">Buyer</th>
                                    <th className="text-right py-2 pr-3">
                                        <SortHeader label="Opens" column="opens" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                                    </th>
                                    <th className="text-right py-2 pr-3">
                                        <SortHeader label="Calculations" column="calculations" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                                    </th>
                                    <th className="text-right py-2 pr-3">
                                        <SortHeader label="Last active" column="last_at" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                                    </th>
                                    <th className="text-right py-2 pl-2">Profile</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {sortedBuyers.map((row) => (
                                    <tr
                                        key={row.buyer.id}
                                        className="hover:bg-gray-50 cursor-pointer"
                                        onClick={() => openBuyer(row.buyer)}
                                    >
                                        <td className="py-2.5 pr-3">
                                            <p className="font-medium text-gray-900">{row.buyer.name}</p>
                                            <p className="text-xs text-gray-500">{row.buyer.email}</p>
                                        </td>
                                        <td className="py-2.5 pr-3 text-right text-gray-900">{row.opens}</td>
                                        <td className="py-2.5 pr-3 text-right text-gray-900">{row.calculations}</td>
                                        <td className="py-2.5 pr-3 text-right text-gray-500">{formatRelative(row.last_at)}</td>
                                        <td className="py-2.5 pl-2 text-right">
                                            <ExternalLink className="w-4 h-4 text-blue-500 inline-block" />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {drilldown && (
                <DrilldownModal
                    title={drilldown.title}
                    filter={drilldown.filter}
                    rows={drilldownRows}
                    loading={drilldownLoading}
                    onClose={() => setDrilldown(null)}
                    onBuyerClick={openBuyer}
                />
            )}
        </div>
    );
};

const DrilldownModal = ({ title, filter, rows, loading, onClose, onBuyerClick }) => {
    const isOpens = filter === 'opens';

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[85vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-5 border-b border-gray-200">
                    <div>
                        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                            {loading ? 'Loading…' : `${rows.length} event${rows.length !== 1 ? 's' : ''}`}
                            {!loading && ' · Click a signed-in buyer to open their CRM profile'}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        aria-label="Close"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>
                <div className="p-5 overflow-auto">
                    {loading ? (
                        <div className="flex items-center justify-center py-12 text-gray-500 text-sm">
                            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                            Loading activity…
                        </div>
                    ) : rows.length === 0 ? (
                        <p className="text-sm text-gray-500">No activity in this window.</p>
                    ) : (
                        <table className="min-w-full text-sm">
                            <thead className="text-xs uppercase text-gray-500 border-b border-gray-200">
                                <tr>
                                    <th className="text-left py-2 pr-3">User</th>
                                    {isOpens ? (
                                        <th className="text-left py-2 pr-3">Property</th>
                                    ) : (
                                        <>
                                            <th className="text-left py-2 pr-3">Mode</th>
                                            <th className="text-right py-2 pr-3">Price</th>
                                            <th className="text-right py-2 pr-3">Cashflow / yr</th>
                                            <th className="text-right py-2 pr-3">Gross yield</th>
                                            <th className="text-right py-2 pr-3">Net yield</th>
                                        </>
                                    )}
                                    <th className="text-right py-2 pr-3">When</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {rows.map((row) => {
                                    const cashflow = row.annual_cash_flow;
                                    const cashflowClass = cashflow == null ? 'text-gray-900' : cashflow >= 0 ? 'text-emerald-600' : 'text-rose-600';
                                    const propLabel = row.property
                                        ? (row.property.address || row.property.title || `Property #${row.property.id}`)
                                        : '—';
                                    return (
                                        <tr
                                            key={row.event_id}
                                            className={row.buyer ? 'hover:bg-gray-50 cursor-pointer' : ''}
                                            onClick={() => row.buyer && onBuyerClick(row.buyer)}
                                        >
                                            <td className="py-2 pr-3">
                                                {row.buyer ? (
                                                    <div>
                                                        <p className="font-medium text-gray-900">{row.buyer.name}</p>
                                                        <p className="text-xs text-gray-500">{row.buyer.email}</p>
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-500 italic">Anonymous visitor</span>
                                                )}
                                            </td>
                                            {isOpens ? (
                                                <td className="py-2 pr-3 text-gray-700 truncate max-w-xs">{propLabel}</td>
                                            ) : (
                                                <>
                                                    <td className="py-2 pr-3 text-gray-700">{modeLabel(row.mode)}</td>
                                                    <td className="py-2 pr-3 text-right text-gray-900">{formatCurrency(row.price)}</td>
                                                    <td className={`py-2 pr-3 text-right font-medium ${cashflowClass}`}>{formatCurrency(cashflow)}</td>
                                                    <td className="py-2 pr-3 text-right text-gray-700">{formatPercent(row.gross_yield_pct)}</td>
                                                    <td className="py-2 pr-3 text-right text-gray-700">{formatPercent(row.net_yield_pct)}</td>
                                                </>
                                            )}
                                            <td className="py-2 pr-3 text-right text-gray-500">{formatRelative(row.occurred_at)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default InvestmentCalculatorAnalytics;
